import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, signal, effect, HostListener, input, output, computed, untracked } from '@angular/core';
import { FormGroup, FormBuilder } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ContributionsFilters, ContributionsOptions, ContributionsFilterState } from '../contributions-filters.types';
import { CONTRIBUTIONS_FILTER_STATES, getDefaultContributionsFilters } from '../contributions-filter-states.constants';
import { PaginatedDeliveries } from '../paginated-deliveries.model';
import { Culture } from '../../cultures';

interface ContributionsSortCriterion {
  sort_by: 'started_at' | 'completed_at' | 'submitted_at' | 'created_at' | 'updated_at';
  sort_order: 'asc' | 'desc';
  default_sort_order: 'asc' | 'desc';
  translationKeyAsc: string;
  translationKeyDesc: string;
  translationScopeAsc?: string;
  translationScopeDesc?: string;
}

@Component({
  selector: 'multisite-contributions-filters',
  templateUrl: './contributions-filters.component.html',
  styleUrls: ['./contributions-filters.component.scss'],

  standalone: false,
})
export class ContributionsFiltersComponent implements OnInit, OnDestroy {

  paginatedDeliveries = input<PaginatedDeliveries | undefined>();
  loading = input<boolean>(false);
  cultures = input<Culture[]>([]);
  changePage = output<number>();
  @Input() initialFilters: ContributionsFilters = {};
  @Input() initialOptions: ContributionsOptions = {
    sort_by: 'updated_at',
    sort_order: 'desc',
    items_per_page: 100,
    page: 1
  };
  
  @Output() filtersChange = new EventEmitter<ContributionsFilters>();
  @Output() optionsChange = new EventEmitter<ContributionsOptions>();

  // Modern Angular signals for reactive state
  private filtersSignal = signal<ContributionsFilters>({});
  private optionsSignal = signal<ContributionsOptions>({
    sort_by: 'started_at',
    sort_order: 'asc',
    items_per_page: 10,
    page: 1
  });
  
  // Public computed signals
  filters = this.filtersSignal.asReadonly();
  options = this.optionsSignal.asReadonly();
  
  // Reactive form for UI controls
  filtersForm: FormGroup;
  
  // Filter state aliases for UI - using shared constants
  filterStates: ContributionsFilterState[] = CONTRIBUTIONS_FILTER_STATES;
  
  // Sort criteria for button-based sorting
  sortCriteria: ContributionsSortCriterion[] = [
    {
      sort_by: 'created_at',
      sort_order: 'asc',
      default_sort_order: 'desc',
      translationKeyAsc: 'common.old',
      translationKeyDesc: 'common.new'
    },
    {
      sort_by: 'started_at',
      sort_order: 'asc',
      default_sort_order: 'asc',
      translationKeyAsc: 'tracking.started',
      translationKeyDesc: 'tracking.started'
    },
    // In future, when the backend supports completed/submitted and therefore locked contributions, restore this:
    // {
    //   sort_by: 'submitted_at',
    //   sort_order: 'asc',
    //   default_sort_order: 'desc',
    //   translationKeyAsc: 'tracking.submitted',
    //   translationKeyDesc: 'tracking.submitted'
    // },
    // {
    //   sort_by: 'completed_at',
    //   sort_order: 'asc',
    //   default_sort_order: 'desc',
    //   translationKeyAsc: 'tracking.completed',
    //   translationKeyDesc: 'tracking.completed'
    // },
    {
      sort_by: 'updated_at',
      sort_order: 'asc',
      default_sort_order: 'desc',
      translationKeyAsc: 'common.updated',
      translationKeyDesc: 'common.updated'
    }
  ];

  // Responsive behavior
  maxWidthMediaQuery: string = '(max-width: 767px)';
  areFiltersCollapsed = true;

  private subscriptions: Subscription[] = [];

  @HostListener('window:resize')
  resizeWindow() {
    this.refreshCollapse();
  }

  constructor(private fb: FormBuilder) {
    // Create reactive form
    this.filtersForm = this.fb.group({
      items_per_page: [this.initialOptions.items_per_page]
    });

    // Effect to emit changes when signals update. The emit is untracked: the parent reads its own signals
    // while handling it, and those must not re-run these effects every time the parent's state changes.
    effect(() => {
      const filters = this.filters();
      untracked(() => this.filtersChange.emit(filters));
    });

    effect(() => {
      const options = this.options();
      untracked(() => this.optionsChange.emit(options));
    });
  }

  doGetPaginatedDeliveries(page: number): void {
    this.changePage.emit(page);
  }

  selectCulture(culture: Culture): void {
    this.filtersSignal.update(current => ({
      ...current,
      culture_id: culture.id
    }));
  }
  deselectCulture(culture: Culture): void {
    this.filtersSignal.update(current => {
      const updatedFilters = { ...current };
      delete updatedFilters.culture_id;
      return updatedFilters;
    });
  }

  selectedCultures = computed(() => {
    return this.cultures().filter(culture => culture.id === this.filters().culture_id);
  });

  ngOnInit(): void {
    // Initialize signals with input values
    this.filtersSignal.set(this.initialFilters);
    this.optionsSignal.set(this.initialOptions);

    // Update form with initial values
    this.filtersForm.patchValue(this.initialOptions);

    // Subscribe to form changes
    const formSubscription = this.filtersForm.valueChanges.subscribe(formValue => {
      this.optionsSignal.update(current => ({
        ...current,
        ...formValue,
        page: 1 // Reset to page 1 when changing options
      }));
    });

    this.subscriptions.push(formSubscription);

    // Set initial collapse state based on screen size
    this.refreshCollapse();
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach(sub => sub.unsubscribe());
  }

  private refreshCollapse(): void {
    if (!window.matchMedia(this.maxWidthMediaQuery).matches) {
      this.areFiltersCollapsed = false;
    } else {
      this.areFiltersCollapsed = true;
    }
  }

  isStateActive(state: ContributionsFilterState): boolean {
    const currentFilters = this.filters();
    return this.filtersMatch(currentFilters, state.filters);
  }

  private filtersMatch(current: ContributionsFilters, target: ContributionsFilters): boolean {
    const targetKeys = Object.keys(target);
    return targetKeys.every(key => current[key] === target[key]);
  }

  selectFilterState(state: ContributionsFilterState): void {
    if (this.isStateActive(state)) {
      if (state.isDefault) {
        // If already active, clear filters
        this.clearAllFilters();
      } else {
        this.setFiltersToDefault();
      }
    } else {
      // Apply the state filters
      this.filtersSignal.set({ ...state.filters });
    }
  }

  clearAllFilters(): void {
    this.filtersSignal.set({});
  }
  setFiltersToDefault(): void {
    const defaultFilters = getDefaultContributionsFilters();
    this.filtersSignal.set({ ...defaultFilters });
  }

  setSortOrderParams(sortable: ContributionsSortCriterion): void {
    const currentOptions = this.options();
    
    if (currentOptions.sort_by === sortable.sort_by) {
      // Same button clicked, toggle the order
      const newOrder: 'asc' | 'desc' = currentOptions.sort_order === 'asc' ? 'desc' : 'asc';
      
      // Update the sort criterion with the new order
      const updatedSortable = { ...sortable, sort_order: newOrder };
      this.updateSortCriteria(updatedSortable);
      
      this.optionsSignal.update(current => ({
        ...current,
        sort_by: sortable.sort_by,
        sort_order: newOrder
      }));
    } else {
      // Different button clicked, use default order
      const updatedSortable = { ...sortable, sort_order: sortable.default_sort_order };
      this.updateSortCriteria(updatedSortable);
      
      this.optionsSignal.update(current => ({
        ...current,
        sort_by: sortable.sort_by,
        sort_order: sortable.default_sort_order
      }));
    }
  }

  private updateSortCriteria(updatedSortable: ContributionsSortCriterion): void {
    this.sortCriteria = this.sortCriteria.map(criterion => 
      criterion.sort_by === updatedSortable.sort_by 
        ? { ...criterion, sort_order: updatedSortable.sort_order }
        : criterion
    );
  }

  clearSorting(): void {
    // Reset to default sorting
    this.optionsSignal.update(current => ({
      ...current,
      sort_by: 'updated_at',
      sort_order: 'desc'
    }));
    
    // Reset sort criteria to defaults
    this.sortCriteria = this.sortCriteria.map(criterion => ({
      ...criterion,
      sort_order: criterion.default_sort_order
    }));
  }

  toggleSortOrder(order: 'asc' | 'desc'): void {
    this.filtersForm.patchValue({ sort_order: order });
  }

  onPageChange(page: number): void {
    this.optionsSignal.update(current => ({
      ...current,
      page
    }));
  }
}